import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import { checkAcceptedFileTypes, validateFileSize } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { infoSx, radioGroupSx, uploadDocSx } from './eligibilityStyles';
import { DocumentLine } from 'containers/tunnel/common';
import {
    CheckboxLabel,
    CustomFormLabel,
    ErrorMessage,
    FileErrorMessage,
    FileInputButton,
    InfoButton,
    RadioLabel,
} from 'components';
import { Box, FormControl, RadioGroup, Typography } from '@mui/material';

const labelSx = { display: 'block', fontSize: '16px', letterSpacing: 0, marginBottom: '10px' };

const filterByName = name => f => f.name !== name;

const ProClientOption = ({
    investorProData,
    setInvestorProData,
    formError,
    setFormError,
    tunnelSubscription,
}) => {
    const [fileSizeError, setFileSizeError] = useState({
        transactionsCompletedDocuments: false,
        portfolioValueDocuments: false,
        professionalPositionHeldDocuments: false,
    });
    const [fileTypeError, setFileTypeError] = useState({
        transactionsCompletedDocuments: false,
        portfolioValueDocuments: false,
        professionalPositionHeldDocuments: false,
    });

    const handleCheckboxChange = useCallback(
        e => {
            const { name, checked } = e.target;
            setInvestorProData(prevState => ({ ...prevState, [name]: checked }));
            if (!checked) {
                setInvestorProData(prevState => ({
                    ...prevState,
                    portfolioValue: '',
                    portfolioValueDocuments: [],
                    transactionsCompleted: '',
                    transactionsCompletedDocuments: [],
                    professionalPositionHeld: '',
                    professionalPositionHeldDocuments: [],
                }));
            }
            setFormError('');
        },
        [setFormError, setInvestorProData]
    );

    const handleRadioChange = useCallback(
        e => {
            const { name, value } = e.target;
            setInvestorProData(prevState => ({ ...prevState, [name]: value }));
            setFormError('');
        },
        [setFormError, setInvestorProData]
    );

    const handleFileChange = useCallback(
        e => {
            const { name, id, files } = e.target;
            const file = files[0];
            checkAcceptedFileTypes(
                file,
                () => {
                    setFileTypeError(prevState => ({ ...prevState, [id]: false }));
                    if (validateFileSize(file)) {
                        setFileSizeError(prevState => ({ ...prevState, [id]: false }));
                        setInvestorProData(prevState => ({
                            ...prevState,
                            [name]: [...prevState[name], file],
                        }));
                    } else {
                        setFileSizeError(prevState => ({ ...prevState, [id]: true }));
                    }
                },
                () => {
                    setFileTypeError(prevState => ({ ...prevState, [id]: true }));
                    setFileSizeError(prevState => ({ ...prevState, [id]: false }));
                }
            );
        },
        [setInvestorProData]
    );

    const handleRemoveFile = useCallback(
        (key, name) => e => {
            setInvestorProData(prevState => ({
                ...prevState,
                [key]: prevState[key].filter(filterByName(name)),
            }));
        },
        [setInvestorProData]
    );

    return (
        <Box display="flex" flexWrap="wrap" justifyContent="flex-start">
            <Box display="flex" position="relative" alignItems="center">
                <CheckboxLabel
                    name="professionalClient"
                    controlSx={{ paddingY: 0 }}
                    sx={{ alignItems: 'flex-start' }}
                    label={texts.SURVEY_RESULTS_PROFESSIONAL_CLIENT}
                    checked={investorProData.professionalClient}
                    onChange={handleCheckboxChange}
                />
                <InfoButton
                    disableMarginBottom
                    title={texts.SURVEY_RESULTS_INFO_BUTTON_TITLE}
                    content={
                        <Typography gutterBottom>
                            {texts.SURVEY_RESULTS_INFO_BUTTON_CONTENT(
                                tunnelSubscription?.investmentFund?.promoter?.name
                            )}
                        </Typography>
                    }
                />
            </Box>
            {investorProData.professionalClient && (
                <Box
                    paddingX={4}
                    paddingY={2}
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                >
                    <Typography gutterBottom sx={infoSx}>
                        {texts.SURVEY_RESULTS_SELECT_HEADER}
                    </Typography>
                    <Typography gutterBottom sx={infoSx}>
                        {texts.SURVEY_RESULTS_SELECT_OPTIONS}
                    </Typography>
                    <FormControl>
                        <CustomFormLabel sx={labelSx}>
                            {texts.SURVEY_RESULTS_HAS_TRANSACTIONS}
                        </CustomFormLabel>
                        <RadioGroup
                            name="transactionsCompleted"
                            sx={radioGroupSx}
                            onChange={handleRadioChange}
                            value={investorProData.transactionsCompleted}
                        >
                            <RadioLabel label="Oui" value="true" />
                            <RadioLabel label="Non" value="false" />
                        </RadioGroup>
                        {investorProData.transactionsCompleted === 'true' && (
                            <Box sx={uploadDocSx}>
                                <Typography gutterBottom>
                                    {texts.SURVEY_RESULTS_HAS_TRANSACTIONS_DOCS}
                                </Typography>
                                <FileInputButton
                                    showIcon
                                    id="transactionsCompletedDocuments"
                                    label={texts.UPLOAD_FILE_BUTTON}
                                    onFileChange={handleFileChange}
                                    noMargin
                                />
                                <Box sx={{ mt: '10px' }}>
                                    {investorProData.transactionsCompletedDocuments.map(doc => (
                                        <DocumentLine
                                            key={doc.name}
                                            doc={doc}
                                            onDelete={handleRemoveFile(
                                                'transactionsCompletedDocuments',
                                                doc.name
                                            )}
                                        />
                                    ))}
                                </Box>
                                {fileSizeError.transactionsCompletedDocuments && (
                                    <FileErrorMessage variant="size" useMargin={false} />
                                )}
                                {fileTypeError.transactionsCompletedDocuments && (
                                    <FileErrorMessage variant="type" useMargin={false} />
                                )}
                            </Box>
                        )}
                    </FormControl>
                    <FormControl>
                        <CustomFormLabel sx={labelSx}>
                            {texts.SURVEY_RESULTS_PORTFOLIO_VALUE}
                        </CustomFormLabel>
                        <RadioGroup
                            name="portfolioValue"
                            sx={radioGroupSx}
                            onChange={handleRadioChange}
                            value={investorProData.portfolioValue}
                        >
                            <RadioLabel label="Oui" value="true" />
                            <RadioLabel label="Non" value="false" />
                        </RadioGroup>
                        {investorProData.portfolioValue === 'true' && (
                            <Box sx={uploadDocSx}>
                                <Typography gutterBottom>
                                    {texts.SURVEY_RESULTS_PORTFOLIO_VALUE_DOCS}
                                </Typography>
                                <FileInputButton
                                    showIcon
                                    id="portfolioValueDocuments"
                                    label={texts.UPLOAD_FILE_BUTTON}
                                    onFileChange={handleFileChange}
                                    noMargin
                                />
                                <Box sx={{ mt: '10px' }}>
                                    {investorProData.portfolioValueDocuments.map(doc => (
                                        <DocumentLine
                                            key={doc.name}
                                            doc={doc}
                                            onDelete={handleRemoveFile(
                                                'portfolioValueDocuments',
                                                doc.name
                                            )}
                                        />
                                    ))}
                                </Box>
                                {fileSizeError.portfolioValueDocuments && (
                                    <FileErrorMessage variant="size" useMargin={false} />
                                )}
                                {fileTypeError.portfolioValueDocuments && (
                                    <FileErrorMessage variant="type" useMargin={false} />
                                )}
                            </Box>
                        )}
                    </FormControl>
                    <FormControl>
                        <CustomFormLabel sx={labelSx}>
                            {texts.SURVEY_RESULTS_PROFESSIONAL_POSITION_HELD}
                        </CustomFormLabel>
                        <RadioGroup
                            name="professionalPositionHeld"
                            sx={radioGroupSx}
                            onChange={handleRadioChange}
                            value={investorProData.professionalPositionHeld}
                        >
                            <RadioLabel label="Oui" value="true" />
                            <RadioLabel label="Non" value="false" />
                        </RadioGroup>
                        {investorProData.professionalPositionHeld === 'true' && (
                            <Box sx={uploadDocSx}>
                                <Typography gutterBottom>
                                    {texts.SURVEY_RESULTS_PROFESSIONAL_POSITION_HELD_DOCS}
                                </Typography>
                                <FileInputButton
                                    showIcon
                                    id="professionalPositionHeldDocuments"
                                    label={texts.UPLOAD_FILE_BUTTON}
                                    onFileChange={handleFileChange}
                                    noMargin
                                />
                                <Box sx={{ mt: '10px' }}>
                                    {investorProData.professionalPositionHeldDocuments.map(doc => (
                                        <DocumentLine
                                            key={doc.name}
                                            doc={doc}
                                            onDelete={handleRemoveFile(
                                                'professionalPositionHeldDocuments',
                                                doc.name
                                            )}
                                        />
                                    ))}
                                </Box>
                                {fileSizeError.professionalPositionHeldDocuments && (
                                    <FileErrorMessage variant="size" useMargin={false} />
                                )}
                                {fileTypeError.professionalPositionHeldDocuments && (
                                    <FileErrorMessage variant="type" useMargin={false} />
                                )}
                            </Box>
                        )}
                    </FormControl>
                </Box>
            )}
            {formError !== '' && <ErrorMessage message={formError} />}
        </Box>
    );
};

ProClientOption.propTypes = {
    investorProData: PropTypes.object,
    setInvestorProData: PropTypes.func,
    formError: PropTypes.string,
    setFormError: PropTypes.func,
};

export default ProClientOption;
